import type {
	Collection,
	CollectionDefaults,
	FindOptions,
	ResolvedCollection,
	StoreCore,
	StoreSchema,
} from '@rstore/shared';
import { visitCollectionsFromFindOptions } from './visit-collections-from-find-options';

interface GetFieldsOptions<
	TStore extends StoreCore<TSchema, TCollectionDefaults>,
	TCollection extends Collection,
	TSchema extends StoreSchema,
	TCollectionDefaults extends CollectionDefaults,
> {
	store: TStore;
	collection: ResolvedCollection<TCollection, TCollectionDefaults, TSchema>;
	findOptions?: FindOptions<TCollection, TCollectionDefaults, TSchema>;
}

/**
 * Determines which fields to fetch, including nested relation fields and their backing primitive fields
 */
export function getFields<
	TStore extends StoreCore<TSchema, TCollectionDefaults>,
	TCollection extends Collection,
	TSchema extends StoreSchema,
	TCollectionDefaults extends CollectionDefaults,
>({ store, collection, findOptions }: GetFieldsOptions<TStore, TCollection, TSchema, TCollectionDefaults>) {
	const fields = [...(findOptions?.fields ?? collection.meta?.defaultFields ?? <string[]>[])];
	const currentPath: string[] = [];

	visitCollectionsFromFindOptions({
		store,
		collection,
		findOptions,
		preVisitNested: (_, relationKey) => currentPath.push(relationKey),
		postVisitNested: () => currentPath.pop(),
		visit: (collection, relationKey, relation) => {
			const path = [...currentPath, relationKey].join('.');
			// add in primitive backing fields of relations
			const targets = relation.to.filter((t) => t.collection === collection.name);
			const relationFields = targets.flatMap((t) => Object.keys(t.on));
			const uniqFields = new Set([...(collection.meta?.defaultFields ?? []), ...relationFields]);
			fields.push(...[...uniqFields].map((field) => `${path}.${field}`));
		},
	});

	return fields;
}
